import React, { useState, useEffect } from 'react';
import { ShoppingBag, ExternalLink, Tag, Sparkles, X, ChevronRight } from 'lucide-react';

/**
 * SmartRecommendations - Contextual product suggestions
 * 
 * Props:
 * - placement: Where the component appears (dashboard, game_details, etc.)
 * - gameId: Optional - for game-specific recommendations
 * - userId: Optional - for personalized recommendations
 * - variant: 'card' | 'inline' | 'sidebar' | 'banner'
 * - onDismiss: Optional - callback when user dismisses
 */
const SmartRecommendations = ({ 
    placement = 'dashboard_featured', 
    gameId = null, 
    userId = null,
    variant = 'card',
    maxProducts = 3,
    showSponsorBadge = true,
    onDismiss = null
}) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        fetchRecommendations();
    }, [placement, gameId, userId]);

    const fetchRecommendations = async () => {
        try {
            const params = new URLSearchParams({ 
                placement,
                limit: maxProducts
            });
            if (gameId) params.append('gameId', gameId);
            if (userId) params.append('userId', userId);

            const res = await fetch(`/api/ads/recommendations?${params}`);
            if (res.ok) {
                const data = await res.json();
                setProducts(data);
            }
        } catch (err) {
            console.error('Recommendations fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = async (product) => {
        // Record click
        try {
            await fetch('/api/ads/click', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    userId,
                    referrerUrl: window.location.href,
                    destinationUrl: product.productUrl
                })
            });
        } catch (err) {
            console.error('Click tracking error:', err);
        }
        
        // Open product URL in new tab
        window.open(product.productUrl, '_blank');
    };

    const handleDismiss = () => {
        setDismissed(true);
        if (onDismiss) onDismiss();
    };

    if (loading) return null;
    if (dismissed) return null;
    if (products.length === 0) return null;

    // Different variants for different placements
    if (variant === 'inline') {
        return (
            <div className="smart-rec-inline">
                <div className="rec-inline-header">
                    <Sparkles size={14} />
                    <span>Suositus</span>
                </div>
                {products.slice(0, 1).map(product => (
                    <button 
                        key={product.id} 
                        className="rec-inline-item"
                        onClick={() => handleClick(product)}
                    >
                        {product.imageUrl && (
                            <img src={product.imageUrl} alt="" className="rec-inline-image" />
                        )}
                        <div className="rec-inline-info">
                            <span className="rec-inline-name">{product.name}</span>
                            <span className="rec-inline-price">
                                {product.salePrice ? (
                                    <>
                                        <s>{product.price}€</s> {product.salePrice}€
                                    </>
                                ) : (
                                    `${product.price}€`
                                )}
                            </span>
                        </div>
                        <ChevronRight size={16} />
                    </button>
                ))}
                <style>{inlineStyles}</style>
            </div>
        );
    }

    if (variant === 'banner') {
        const product = products[0];
        return (
            <div className="smart-rec-banner">
                {onDismiss && (
                    <button className="rec-banner-close" onClick={handleDismiss}>
                        <X size={14} />
                    </button>
                )}
                <div className="rec-banner-content" onClick={() => handleClick(product)}>
                    {product.imageUrl && (
                        <img src={product.imageUrl} alt="" className="rec-banner-image" />
                    )}
                    <div className="rec-banner-info">
                        <div className="rec-banner-badge">
                            <Tag size={12} /> {getReasonLabel(product.matchReason)}
                        </div>
                        <h4>{product.name}</h4>
                        <p>{product.description?.substring(0, 80)}...</p>
                        <div className="rec-banner-footer">
                            <span className="rec-banner-price">
                                {product.salePrice ? (
                                    <>
                                        <span className="original">{product.price}€</span>
                                        <span className="sale">{product.salePrice}€</span>
                                    </>
                                ) : (
                                    <span className="sale">{product.price}€</span>
                                )}
                            </span>
                            <span className="rec-banner-sponsor">
                                {product.sponsorName}
                            </span>
                        </div>
                    </div>
                    <ExternalLink size={16} className="rec-banner-link" />
                </div>
                <style>{bannerStyles}</style>
            </div>
        );
    }

    // Default card variant
    return (
        <div className="smart-rec-container">
            <div className="rec-header">
                <div className="rec-title">
                    <Sparkles size={16} />
                    <span>Suositukset sinulle</span>
                </div>
                {onDismiss && (
                    <button className="rec-dismiss" onClick={handleDismiss}>
                        <X size={16} />
                    </button>
                )}
            </div>

            <div className="rec-products">
                {products.map(product => (
                    <div key={product.id} className="rec-product-card">
                        <div className="rec-product-image">
                            {product.imageUrl ? (
                                <img src={product.imageUrl} alt={product.name} />
                            ) : (
                                <div className="rec-product-placeholder">
                                    <ShoppingBag size={24} />
                                </div>
                            )}
                            {product.salePrice && (
                                <span className="rec-sale-badge">
                                    -{Math.round((1 - product.salePrice / product.price) * 100)}%
                                </span>
                            )}
                        </div>
                        
                        <div className="rec-product-info">
                            <span className="rec-match-reason">
                                {getReasonLabel(product.matchReason)}
                            </span>
                            <h4 className="rec-product-name">{product.name}</h4>
                            
                            <div className="rec-product-price">
                                {product.salePrice ? (
                                    <>
                                        <span className="rec-price-original">{product.price}€</span>
                                        <span className="rec-price-sale">{product.salePrice}€</span>
                                    </>
                                ) : (
                                    <span className="rec-price-sale">{product.price}€</span>
                                )}
                            </div>
                        </div>
                        
                        <button 
                            className="rec-product-button"
                            onClick={() => handleClick(product)}
                        >
                            <span>Katso</span>
                            <ExternalLink size={14} />
                        </button>
                        
                        {showSponsorBadge && (
                            <div className="rec-sponsor">
                                {product.sponsorLogo && (
                                    <img src={product.sponsorLogo} alt="" className="rec-sponsor-logo" />
                                )}
                                <span>{product.sponsorName}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <style>{cardStyles}</style>
        </div>
    );
};

// Helper to translate match reasons
function getReasonLabel(reason) {
    const labels = {
        'game_specific': '🎯 Tälle pelille',
        'category_match': '📂 Sinun tyylisi',
        'recently_viewed': '👁️ Katsomasi peli',
        'featured': '⭐ Suositeltu',
        'ai_suggestion': '🤖 Tekoälyn suositus'
    };
    return labels[reason] || '💡 Suositus';
}

// Styles for card variant
const cardStyles = `
    .smart-rec-container {
        background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
        border: 1px solid rgba(139, 92, 246, 0.3);
        border-radius: 12px;
        padding: 1rem;
        margin: 1rem 0;
    }

    .rec-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;
    }

    .rec-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #a78bfa;
        font-weight: 600;
        font-size: 0.9rem;
    }

    .rec-dismiss {
        background: transparent;
        border: none;
        color: #6b7280;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 4px;
    }

    .rec-dismiss:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #9ca3af;
    }

    .rec-products {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 0.75rem;
    }

    .rec-product-card {
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid #374151;
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: all 0.2s;
    }

    .rec-product-card:hover {
        border-color: #a78bfa;
        transform: translateY(-2px);
    }

    .rec-product-image {
        position: relative;
        height: 100px;
        background: #1f2937;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .rec-product-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .rec-product-placeholder {
        color: #4b5563;
    }

    .rec-sale-badge {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background: #ef4444;
        color: white;
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-size: 0.7rem;
        font-weight: bold;
    }

    .rec-product-info {
        padding: 0.75rem;
        flex: 1;
    }

    .rec-match-reason {
        font-size: 0.7rem;
        color: #a78bfa;
        margin-bottom: 0.25rem;
        display: block;
    }

    .rec-product-name {
        margin: 0 0 0.5rem 0;
        font-size: 0.85rem;
        color: #f0f6fc;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .rec-product-price {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .rec-price-original {
        color: #6b7280;
        text-decoration: line-through;
        font-size: 0.8rem;
    }

    .rec-price-sale {
        color: #22c55e;
        font-weight: bold;
        font-size: 1rem;
    }

    .rec-product-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.6rem;
        background: rgba(139, 92, 246, 0.2);
        border: none;
        border-top: 1px solid #374151;
        color: #a78bfa;
        cursor: pointer;
        font-size: 0.85rem;
        font-weight: 500;
        transition: background 0.2s;
    }

    .rec-product-button:hover {
        background: rgba(139, 92, 246, 0.4);
    }

    .rec-sponsor {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.4rem 0.75rem;
        background: rgba(0, 0, 0, 0.2);
        font-size: 0.7rem;
        color: #6b7280;
    }

    .rec-sponsor-logo {
        width: 16px;
        height: 16px;
        border-radius: 2px;
    }

    @media (max-width: 768px) {
        .rec-products {
            grid-template-columns: 1fr;
        }
        
        .rec-product-card {
            flex-direction: row;
        }
        
        .rec-product-image {
            width: 80px;
            height: auto;
        }
        
        .rec-product-info {
            flex: 1;
        }
        
        .rec-product-button {
            border-top: none;
            border-left: 1px solid #374151;
            padding: 0.75rem;
        }
        
        .rec-sponsor {
            display: none;
        }
    }
`;

// Styles for inline variant
const inlineStyles = `
    .smart-rec-inline {
        background: rgba(139, 92, 246, 0.1);
        border: 1px solid rgba(139, 92, 246, 0.2);
        border-radius: 8px;
        padding: 0.5rem;
        margin: 0.5rem 0;
    }

    .rec-inline-header {
        display: flex;
        align-items: center;
        gap: 0.3rem;
        color: #a78bfa;
        font-size: 0.7rem;
        margin-bottom: 0.4rem;
        padding-left: 0.25rem;
    }

    .rec-inline-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.4rem;
        background: rgba(0, 0, 0, 0.2);
        border: 1px solid transparent;
        border-radius: 6px;
        cursor: pointer;
        text-align: left;
        color: inherit;
        transition: all 0.2s;
    }

    .rec-inline-item:hover {
        border-color: #a78bfa;
        background: rgba(139, 92, 246, 0.1);
    }

    .rec-inline-image {
        width: 40px;
        height: 40px;
        border-radius: 4px;
        object-fit: cover;
    }

    .rec-inline-info {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rec-inline-name {
        font-size: 0.85rem;
        color: #f0f6fc;
        font-weight: 500;
    }

    .rec-inline-price {
        font-size: 0.75rem;
        color: #22c55e;
    }

    .rec-inline-price s {
        color: #6b7280;
        margin-right: 0.3rem;
    }
`;

// Styles for banner variant
const bannerStyles = `
    .smart-rec-banner {
        position: relative;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
        border: 1px solid rgba(139, 92, 246, 0.3);
        border-radius: 12px;
        overflow: hidden;
        margin: 1rem 0;
    }

    .rec-banner-close {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background: rgba(0, 0, 0, 0.5);
        border: none;
        color: #9ca3af;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
    }

    .rec-banner-close:hover {
        background: rgba(0, 0, 0, 0.7);
        color: #fff;
    }

    .rec-banner-content {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        cursor: pointer;
        transition: background 0.2s;
    }

    .rec-banner-content:hover {
        background: rgba(139, 92, 246, 0.1);
    }

    .rec-banner-image {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        object-fit: cover;
        flex-shrink: 0;
    }

    .rec-banner-info {
        flex: 1;
    }

    .rec-banner-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.2rem 0.5rem;
        background: rgba(139, 92, 246, 0.3);
        border-radius: 4px;
        font-size: 0.7rem;
        color: #c4b5fd;
        margin-bottom: 0.5rem;
    }

    .rec-banner-info h4 {
        margin: 0 0 0.25rem 0;
        color: #f0f6fc;
        font-size: 1rem;
    }

    .rec-banner-info p {
        margin: 0;
        color: #9ca3af;
        font-size: 0.85rem;
    }

    .rec-banner-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 0.5rem;
    }

    .rec-banner-price .original {
        color: #6b7280;
        text-decoration: line-through;
        font-size: 0.8rem;
        margin-right: 0.5rem;
    }

    .rec-banner-price .sale {
        color: #22c55e;
        font-weight: bold;
        font-size: 1.1rem;
    }

    .rec-banner-sponsor {
        font-size: 0.75rem;
        color: #6b7280;
    }

    .rec-banner-link {
        color: #a78bfa;
        flex-shrink: 0;
    }

    @media (max-width: 768px) {
        .rec-banner-content {
            flex-direction: column;
            text-align: center;
        }
        
        .rec-banner-footer {
            flex-direction: column;
            gap: 0.5rem;
        }
    }
`;

export default SmartRecommendations;


